import type { VariantKind, VariantRule } from './variantShape'

/**
 * The template catalogue (F-MOD-N12).
 *
 * A template is a shared list the owner has not typed yet. Two places read
 * it: the product form's "Add a question" box suggests single templates by
 * name, and the Shared lists page installs a whole cuisine pack at once.
 *
 * `links` is the one cross-group rule we ship pre-wired: picking a cup size
 * retunes how many scoops the Scoops list asks for. It names the target by
 * template key because neither list has an id until it is created.
 */
export interface TemplateChoice {
  name: string
  price?: number
  isDefault?: boolean
  /** Cross-group rule: when this choice is picked, `target` asks for min..max. */
  links?: { target: string; min: number; max: number }
}

export interface VariantTemplate {
  /** Stable key, unique across the catalogue. */
  key: string
  name: string
  prompt: string
  kind: VariantKind
  rule: VariantRule
  choices: TemplateChoice[]
}

export interface TemplatePack {
  key: string
  name: string
  /** Who this is for, in the owner's words. */
  blurb: string
  templates: VariantTemplate[]
}

const t = (
  key: string,
  name: string,
  prompt: string,
  kind: VariantKind,
  rule: VariantRule,
  choices: Array<string | TemplateChoice>,
): VariantTemplate => ({
  key,
  name,
  prompt,
  kind,
  rule,
  choices: choices.map((c) => (typeof c === 'string' ? { name: c } : c)),
})

const PICK_ONE: VariantRule = { mode: 'required', n: 1, m: 1 }
const OPTIONAL_ONE: VariantRule = { mode: 'optional', n: 0, m: 1 }
const ANY: VariantRule = { mode: 'any', n: 0, m: null }
const UPTO = (m: number): VariantRule => ({ mode: 'upto', n: 0, m })
const SIZE: VariantRule = { mode: 'exactly', n: 1, m: 1 }

export const TEMPLATE_PACKS: TemplatePack[] = [
  {
    key: 'coffee',
    name: 'Coffee & cafés',
    blurb: 'Sizes, milks, hot or iced, extras and sweetness.',
    templates: [
      t('coffee.size', 'Size', 'Which size?', 'size', SIZE, [{ name: 'Regular', isDefault: true }, 'Large']),
      t('coffee.milk', 'Milk', 'Which milk?', 'one', PICK_ONE, [{ name: 'Full cream', isDefault: true }, 'Skimmed', 'Oat', 'Almond']),
      t('coffee.temp', 'Temperature', 'Hot or iced?', 'one', PICK_ONE, [{ name: 'Hot', isDefault: true }, 'Iced']),
      t('coffee.extras', 'Extras', 'Any extras?', 'many', UPTO(3), ['Extra shot', 'Vanilla syrup', 'Caramel syrup', 'Whipped cream']),
      t('coffee.sweet', 'Sweetness', 'How sweet?', 'one', OPTIONAL_ONE, ['No sugar', 'Less sugar', { name: 'Regular', isDefault: true }, 'Extra sweet']),
    ],
  },
  {
    key: 'chai',
    name: 'Chai & karak',
    blurb: 'Cup size, sugar, strength and the usual additions.',
    templates: [
      t('chai.cup', 'Cup', 'Which size?', 'size', SIZE, ['Small', { name: 'Regular', isDefault: true }, 'Large']),
      t('chai.sugar', 'Sugar', 'How much sugar?', 'one', OPTIONAL_ONE, ['No sugar', 'Less sugar', { name: 'Regular', isDefault: true }, 'Extra sweet']),
      t('chai.strength', 'Strength', 'How strong?', 'one', OPTIONAL_ONE, ['Light', { name: 'Regular', isDefault: true }, 'Kadak']),
      t('chai.add', 'Additions', 'Anything added?', 'many', ANY, ['Elaichi', 'Zafran', 'Extra milk', 'Ginger']),
    ],
  },
  {
    key: 'desi',
    name: 'Desi & BBQ',
    blurb: 'Half or full portions, spice level, bread and sides.',
    templates: [
      t('desi.portion', 'Portion', 'Which portion?', 'size', SIZE, ['Half', { name: 'Full', isDefault: true }]),
      t('desi.spice', 'Spice', 'How spicy?', 'one', PICK_ONE, ['Mild', { name: 'Medium', isDefault: true }, 'Hot', 'Extra hot']),
      t('desi.bread', 'Bread', 'Which bread?', 'one', OPTIONAL_ONE, ['Naan', 'Roti', 'Garlic naan', 'Paratha']),
      t('desi.sides', 'Sides', 'Any sides?', 'many', ANY, ['Raita', 'Salad', 'Chutney', 'Extra gravy']),
    ],
  },
  {
    key: 'burgers',
    name: 'Burgers & fast food',
    blurb: 'Meal upgrades, patty count, hold-the-onions and extras.',
    templates: [
      t('burgers.meal', 'Make it a meal', 'Make it a meal?', 'one', PICK_ONE, [{ name: 'No, just the burger', isDefault: true }, 'Regular meal', 'Large meal']),
      t('burgers.patty', 'Patty', 'Single or double?', 'size', SIZE, [{ name: 'Single', isDefault: true }, 'Double']),
      t('burgers.remove', 'Remove', 'Leave anything out?', 'many', ANY, ['No onions', 'No pickles', 'No mayo', 'No lettuce', 'No tomato']),
      t('burgers.extras', 'Extras', 'Any extras?', 'many', ANY, ['Cheese', 'Extra patty', 'Egg', 'Jalapeños']),
    ],
  },
  {
    key: 'pizza',
    name: 'Pizza',
    blurb: 'Size, crust, toppings and dips.',
    templates: [
      t('pizza.size', 'Size', 'Which size?', 'size', SIZE, ['9"', { name: '12"', isDefault: true }, '16"']),
      t('pizza.crust', 'Crust', 'Which crust?', 'one', PICK_ONE, ['Thin', { name: 'Pan', isDefault: true }, 'Stuffed crust']),
      t('pizza.toppings', 'Toppings', 'Any extra toppings?', 'many', UPTO(5), ['Extra cheese', 'Chicken', 'Mushroom', 'Olives', 'Jalapeños', 'Onion', 'Capsicum']),
      t('pizza.dips', 'Dips', 'Any dips?', 'many', ANY, ['Garlic', 'Chilli', 'Ranch']),
    ],
  },
  {
    key: 'icecream',
    name: 'Ice cream & desserts',
    blurb: 'Cup size that decides the scoop count, flavours, toppings.',
    templates: [
      t('icecream.cup', 'Cup', 'Which cup?', 'size', SIZE, [
        { name: 'Small', isDefault: true, links: { target: 'icecream.scoops', min: 1, max: 1 } },
        { name: 'Medium', links: { target: 'icecream.scoops', min: 2, max: 2 } },
        { name: 'Large', links: { target: 'icecream.scoops', min: 3, max: 3 } },
      ]),
      t('icecream.scoops', 'Scoops', 'Which flavours?', 'many', { mode: 'exactly', n: 1, m: 1 }, ['Vanilla', 'Chocolate', 'Strawberry', 'Pistachio', 'Mango', 'Cookies & cream']),
      t('icecream.toppings', 'Toppings', 'Any toppings?', 'many', UPTO(3), ['Sprinkles', 'Chocolate sauce', 'Nuts', 'Brownie bits']),
    ],
  },
  {
    key: 'drinks',
    name: 'Drinks & juices',
    blurb: 'Bottle sizes, ice and sweetness.',
    templates: [
      t('drinks.size', 'Size', 'Which size?', 'size', SIZE, ['250 ml', { name: '500 ml', isDefault: true }, '1 L']),
      t('drinks.ice', 'Ice', 'How much ice?', 'one', OPTIONAL_ONE, ['No ice', 'Less ice', { name: 'Regular', isDefault: true }]),
      t('drinks.sweet', 'Sweetness', 'How sweet?', 'one', OPTIONAL_ONE, ['No sugar', 'Less sugar', { name: 'Regular', isDefault: true }]),
    ],
  },
  {
    key: 'shawarma',
    name: 'Shawarma & wraps',
    blurb: 'Size, sauce, leave-outs and extras.',
    templates: [
      t('shawarma.size', 'Size', 'Which size?', 'size', SIZE, [{ name: 'Regular', isDefault: true }, 'Large']),
      t('shawarma.sauce', 'Sauce', 'Which sauce?', 'one', PICK_ONE, [{ name: 'Garlic', isDefault: true }, 'Chilli', 'Both', 'None']),
      t('shawarma.remove', 'Remove', 'Leave anything out?', 'many', ANY, ['No pickles', 'No fries inside', 'No onions']),
      t('shawarma.extras', 'Extras', 'Any extras?', 'many', ANY, ['Extra meat', 'Cheese', 'Extra sauce']),
    ],
  },
]

/** Every template, flat, for the suggestion box. */
export const ALL_TEMPLATES: VariantTemplate[] = TEMPLATE_PACKS.flatMap((p) => p.templates)

/** Templates whose name or prompt matches what the owner typed. */
export function matchTemplates(query: string, kinds?: VariantKind[]): VariantTemplate[] {
  const q = query.trim().toLowerCase()
  const seen = new Set<string>()
  return ALL_TEMPLATES.filter((tpl) => {
    if (kinds && !kinds.includes(tpl.kind)) return false
    if (q && !tpl.name.toLowerCase().includes(q) && !tpl.prompt.toLowerCase().includes(q)) return false
    // One "Size" is enough in a dropdown, whichever cuisine it came from.
    const dedupe = `${tpl.kind}:${tpl.name.toLowerCase()}`
    if (seen.has(dedupe)) return false
    seen.add(dedupe)
    return true
  })
}
